import { get } from 'lodash-es';
import { LINK_TYPE, LINK_META_CATEGORY, WidgetTypes } from '@/constants/linkWidget';
import { ContentMeta, WidgetMeta } from './linkUtils';
import log, { LOG_TYPE } from './log';
import { ContentTypes } from '@/constants/linkContent';

/**
 *
 * @param {String} search - string to search
 * @param {Object} link - link
 * @param {Object} link.data - link data
 * @param {string[]} link.paths - paths to searched value
 * @returns {boolean} - returns `true` if `search` value matched overwise `false`;
 */
function findMatch(search, { data = {}, paths = [] }) {
  let matchedTimes = 0;
  let i = 0;

  while (i < paths.length && matchedTimes < 1) {
    const path = paths[i];
    const valueToSearch = get(data, path);

    if (typeof valueToSearch === 'string') {
      const isFieldMatched = valueToSearch.toLowerCase().includes(search);
      if (isFieldMatched) {
        matchedTimes += 1;
      }
    } else {
      log(`No string field ('${path}') inside a link object`);
    }

    i += 1;
  }

  return matchedTimes > 0;
}

/**
 * Wrapper for `findMatch` function
 * @param {String} search - string to search
 * @param {Object} data - link data
 * @returns {Function} - returns wrapped `findMatch`
 */
function prepareFindMatch(search, data, defaultPaths = []) {
  const searchQuery = search.toLowerCase();
  return paths => findMatch(searchQuery, { data, paths: [...defaultPaths, ...paths] });
}

/**
 * Process filling of `filteredList` if matched
 * @param {Object[]} filteredList
 * @param {Object} link
 * @returns {void}
 */
function prepareProcessFilteredLinks(filteredList, link) {
  return function processFilteredLinks(isMatched = false) {
    if (isMatched) {
      filteredList.push(link);
    }
  };
}

/**
 * Search `search` value in `links` list
 * @param {String} search - string to search
 * @param {Object[]} links - list of links
 * @returns filtered `links` list
 */
function filterLinks(search, links) {
  if (!search) return links;

  const filteredList = [];
  for (let i = 0; i < links.length; i += 1) {
    const link = links[i];
    const { linkType } = link;
    const processFilteredLinks = prepareProcessFilteredLinks(filteredList, link);

    switch (linkType) {
      case LINK_TYPE.WIDGET: {
        const {
          type: widgetType,
          category: widgetCategory = '',
        } = link.data;
        const widgetMeta = new WidgetMeta(LINK_META_CATEGORY.DEFAULT, { type: widgetType, category: widgetCategory });

        const findMatchWrapper = prepareFindMatch(search, {
          ...link.data,
          _widgetName: widgetMeta.getTitle(),
        }, ['_widgetName']);


        switch (widgetType) {
          case WidgetTypes.LINK: {
            const isMatched = findMatchWrapper(['name']);
            processFilteredLinks(isMatched);
            break;
          }
          case WidgetTypes.SUPPORT:
          case WidgetTypes.SUBSCRIPTION:
          case WidgetTypes.EMAIL_CAPTURE: {
            const isMatched = findMatchWrapper(['title']);
            processFilteredLinks(isMatched);
            break;
          }
          case WidgetTypes.HEADER: {
            const isMatched = findMatchWrapper(['title']);
            processFilteredLinks(isMatched);
            break;
          }
          default:
            log(`Unknown widget type - ${widgetType}`, LOG_TYPE.ERROR);
        }
        break;
      }
      case LINK_TYPE.CONTENT: {
        const { type: contentType } = link.data;
        const contentMeta = new ContentMeta(LINK_META_CATEGORY.DEFAULT, { type: contentType });

        const findMatchWrapper = prepareFindMatch(search, {
          ...link.data,
          _contentName: contentMeta.getTitle(),
        }, ['_contentName']);

        switch (contentType) {
          case ContentTypes.FILE:
          case ContentTypes.RECIPE:
          case ContentTypes.ARTICLE:
          case ContentTypes.LIST:
          case ContentTypes.PODCAST:
          case ContentTypes.VIDEO:
          case ContentTypes.COURSE:
          case ContentTypes.ONE_OFF_EVENT:
          case ContentTypes.EXTRAS: {
            const isMatched = findMatchWrapper(['title', 'body']);
            processFilteredLinks(isMatched);
            break;
          }
          default:
            log(`Unknown content type - ${contentType}`, LOG_TYPE.ERROR);
        }
        break;
      }
      default:
        log(`Unknown link type - ${linkType}`, LOG_TYPE.ERROR);
    }
  }

  return filteredList;
}

export default filterLinks;
